import { CanvasActionTypes } from '@storeOld/reducers/canvas/canvas'
import { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import useKeyPressed from '@/hooks/useKeyPressed'
import { filterMap } from '@/utils/arrays'
import { hasRectIntersection, type Rectangle } from '@/utils/math'
import { keyboardKeys } from '@/utils/os/keys'
import css from './PointerSelection.module.scss'

const OVERLAY_PX = 2

export interface PointerSelectionHandle {
  onChangeSelection: (rect: Rectangle | null) => void
}

const PointerSelection = forwardRef<PointerSelectionHandle>((_, ref) => {
  const dispatch = useDispatch()

  const [pointerSelectionRect, setPointerSelectionRect] =
    useState<Rectangle | null>()

  const visibleNodeTargetsRef = useRef<Element[]>([])

  const shiftKeyPressed = useKeyPressed(keyboardKeys().shift)

  useImperativeHandle(ref, () => {
    const onChangeSelection = (newRect: Rectangle | null) => {
      setPointerSelectionRect((lastRect) => {
        if (!lastRect && newRect) {
          // Start selection
          const intersectionObserver = new IntersectionObserver((entries) => {
            intersectionObserver.disconnect()

            visibleNodeTargetsRef.current = filterMap(
              entries,
              (e) => e.isIntersecting,
              (e) => e.target
            )
          })
          document.querySelectorAll('.Tree-node').forEach((node) => {
            intersectionObserver.observe(node)
          })
        }

        if (lastRect && !newRect) {
          // End selection
          const selectNodeIds = filterMap(
            visibleNodeTargetsRef.current,
            (target) => {
              const nodeRect = target.getBoundingClientRect()
              return hasRectIntersection(nodeRect, lastRect, OVERLAY_PX)
            },
            (target) => target.id
          )

          const type = shiftKeyPressed
            ? CanvasActionTypes.ADD_SELECTED_NODES
            : CanvasActionTypes.SELECT_NODES

          dispatch({
            type,
            payload: selectNodeIds,
          })
        }

        return newRect
      })
    }

    return {
      onChangeSelection,
    }
  }, [shiftKeyPressed])

  if (!pointerSelectionRect) {
    return null
  }

  return (
    <div
      className={css.selection}
      style={{
        left: pointerSelectionRect.x,
        top: pointerSelectionRect.y,
        width: pointerSelectionRect.width,
        height: pointerSelectionRect.height,
      }}
    />
  )
})

export default PointerSelection
